import {
  SupplyHubPurchaseOrder,
  SupplyHubVendorLocation,
  SupplyHubWarehouse,
} from '../../../types';

export type SupplyHubShippingMode = 'air' | 'sea' | 'truck' | 'pickup' | 'other';

export const SUPPLY_HUB_SHIPPING_MODE_CONFIG: Record<
  SupplyHubShippingMode,
  { color: string; label: string; glyph: string; lineDash?: number[] }
> = {
  air: { color: '#38bdf8', label: 'Air', glyph: '\u2708', lineDash: [6, 4] },
  sea: { color: '#2563eb', label: 'Sea', glyph: '\U0001F6A2' },
  truck: { color: '#f97316', label: 'Truck', glyph: '\U0001F69A', lineDash: [2, 6] },
  pickup: { color: '#22c55e', label: 'Pickup', glyph: '\U0001F4E6', lineDash: [1, 3] },
  other: { color: '#94a3b8', label: 'Other', glyph: '\u2753', lineDash: [1, 4] },
};


const SUPPLY_HUB_SHIPPING_PATTERNS: Record<Exclude<SupplyHubShippingMode, 'other'>, string[]> = {
  air: ['air', 'flight', 'airway', 'air freight', 'airfreight', 'air cargo'],
  sea: ['sea', 'ocean', 'vessel', 'ship', 'shipping', 'seafreight', 'sea freight'],
  truck: ['truck', 'road', 'ground', 'lorry', 'trucking', 'carrier'],
  pickup: ['pickup', 'pick up', 'pick-up', 'collection', 'collect'],
};

export const resolveSupplyHubShippingMode = (value?: string): SupplyHubShippingMode => {
  if (typeof value !== 'string') {
    return 'other';
  }

  const normalized = value.toLowerCase();
  for (const [mode, keywords] of Object.entries(SUPPLY_HUB_SHIPPING_PATTERNS)) {
    if (keywords.some((keyword) => normalized.includes(keyword))) {
      return mode as SupplyHubShippingMode;
    }
  }

  return 'other';
};

export type SupplyHubShippingSummary = Record<
  SupplyHubShippingMode,
  {
    count: number;
    orders: SupplyHubPurchaseOrder[];
    totalValue: number;
  }
>;

export interface SupplyHubVendorGroup {
  key: string;
  vendorId: string;
  vendorName: string;
  location: SupplyHubVendorLocation;
  warehouse: SupplyHubWarehouse;
  purchaseOrders: SupplyHubPurchaseOrder[];
  totalOrders: number;
  totalValue: number;
  shippingSummary: SupplyHubShippingSummary;
}

export type SupplyHubDrawerContext =
  | {
      type: 'warehouse';
      warehouse: SupplyHubWarehouse;
      vendorGroups: SupplyHubVendorGroup[];
      totalPurchaseOrders: number;
    }
  | {
      type: 'vendor';
      vendorGroup: SupplyHubVendorGroup;
    }
  | {
      type: 'route';
      vendorGroup: SupplyHubVendorGroup;
      shippingMode: SupplyHubShippingMode;
      orders: SupplyHubPurchaseOrder[];
    };

export const buildSupplyHubWarehouseKey = (warehouse: SupplyHubWarehouse): string => {
  if (warehouse.warehouse_id !== undefined && warehouse.warehouse_id !== null) {
    return `id-${warehouse.warehouse_id}`;
  }
  if (warehouse.warehouse_name) {
    return `name-${warehouse.warehouse_name}`;
  }
  return 'warehouse-unknown';
};
